import React, { useState, useEffect, useCallback } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Card,
  CardContent,
  // Grid,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
// import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { authToken } from '../globals/Global'; // Assuming this is your global token
import { createYoutubeVideo, getYoutubeVideos } from '../globals/Services';

// ✅ Add this helper function here
const extractYouTubeId = (url) => {
  const match = url.match(/(?:v=|\/)([0-9A-Za-z_-]{11})(?:&|$)/);
  return match ? match[1] : '';
};

export default function AartiManagement() {
  // State for categories (e.g., God names)
  const [categories, setCategories] = useState([]);
  // State for all sub-categories (e.g., Aarti content)
  const [subCategories, setSubCategories] = useState([]);

  // State for form inputs for creating a new category
  const [newCategory, setNewCategory] = useState({ titleName: '', name: '', imageUrl: '', imageFile: null, });

  // State for form inputs for creating a new sub-category
  const [newSubCategory, setNewSubCategory] = useState({
  videoId: '',            // selected YouTube video
  videoContent: '',       // description
  videoUrls: '',          // YouTube URL
});


  // State to manage which category is currently selected in the UI
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  // --- Dialog Management State ---
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openEditCategoryDialog, setOpenEditCategoryDialog] = useState(false);
  const [openEditSubCategoryDialog, setOpenEditSubCategoryDialog] = useState(false);
  const [openDeleteCategoryDialog, setOpenDeleteCategoryDialog] = useState(false);
  const [openDeleteSubCategoryDialog, setOpenDeleteSubCategoryDialog] = useState(false);

  // --- State for Items Being Edited or Deleted ---
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubCategory, setEditingSubCategory] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [deletingSubCategory, setDeletingSubCategory] = useState(null);

  // --- State to trigger data refetching ---
  const [refetch, setRefetch] = useState(0);
  const triggerRefetch = () => setRefetch(prev => prev + 1);

  // --- Data Fetching ---
  const fetchAartiData = useCallback(async () => {
    try {
      const token = authToken || localStorage.getItem('sessionToken');
      const res = await fetch(getYoutubeVideos, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch data');
      }

      const result = await res.json();
      const aartiData = result?.data || [];

      const transformedCategories = aartiData.map(cat => ({
        id: cat.id,
        titleName: cat.title,
        // name: cat.aartiGodName,
        imageUrl: cat.imageUrl || '',
      }));
      setCategories(transformedCategories);

      const allSubCategories = aartiData.map(cat => ({
        id: cat.youtubeVideoContentDetails.id,
        aartiCategoryId: cat.id,
        name: cat.title,
        description: cat.youtubeVideoContentDetails.videoContent,
        videoUrl: cat.youtubeVideoContentDetails.videoUrls, // corrected key
        }));

      setSubCategories(allSubCategories);

      if (!selectedCategoryId && transformedCategories.length > 0) {
        setSelectedCategoryId(transformedCategories[0].id);
      }
      
      if (transformedCategories.length > 0) {
          setNewSubCategory(prev => ({ ...prev, aartiCategoryId: prev.aartiCategoryId || transformedCategories[0].id }));
      }

    } catch (error) {
      console.error('Failed to fetch Aarti data:', error);
    }
  }, [selectedCategoryId]);

  useEffect(() => {
    fetchAartiData();
  }, [refetch, fetchAartiData]);

  const getAuthToken = () => authToken || localStorage.getItem('sessionToken');

  // --- Category CRUD Handlers ---
  const handleCreateCategory = async () => {
  try {
    const formData = new FormData();
    formData.append('title', newCategory.titleName);
    formData.append('videoContent', newSubCategory.description);
    formData.append('videoUrls', newSubCategory.videoUrl);
    formData.append('file', newCategory.imageFile); // this must be a File object

    const res = await fetch(createYoutubeVideo, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAuthToken()}`
        // Do NOT add 'Content-Type' manually with FormData!
      },
      body: formData,
    });

    const result = await res.json();
    if (!res.ok || result.status !== 'SUCCESS') {
      throw new Error(result.errorMessage || 'Could not upload video.');
    }

    toast.success('Video uploaded successfully!');
    setOpenCategoryDialog(false);
    setNewCategory({ titleName: '', name: '', imageUrl: '', imageFile: null });
    setNewSubCategory({ ...newSubCategory, videoUrl: '', description: '' });
    triggerRefetch();
  } catch (error) {
    console.error('Upload error:', error);
    toast.error(`Error: ${error.message}`);
  }
};


  const handleUpdateCategory = async () => {
    if (!editingCategory) return;
    try {
      const res = await fetch('/api/aartiDetails/aarti/updateAartiByAdmin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
        body: JSON.stringify({
          aartiId: Number(editingCategory.id),
          aartiTitleName: editingCategory.titleName,
          aartiGodName: editingCategory.name,
          aartiGodImageUrl: editingCategory.imageUrl,
        }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || 'Could not update category.');

      toast.success('Category updated successfully!');
      setOpenEditCategoryDialog(false);
      triggerRefetch();
    } catch (error) {
        console.error('API error:', error);
        toast.error(`Error: ${error.message}`);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deletingCategory) return;
    try {
      const res = await fetch(`/api/aartiDetails/aarti/deleteAartiByAdmin/${deletingCategory.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });

      if (!res.ok) {
        const result = await res.json();
        throw new Error(result.message || 'Could not delete category.');
      }

      toast.success('Category deleted successfully!');
      setOpenDeleteCategoryDialog(false);
      setSelectedCategoryId(null);
      triggerRefetch();
    } catch (error) {
        console.error('API error:', error);
        toast.error(`Error: ${error.message}`);
    }
  };
  
  const handleUpdateSubCategory = async () => {
    if (!editingSubCategory) return;
    try {
      const res = await fetch('/api/aartiDetails/aarti/updateAartiContentByAdmin', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
          body: JSON.stringify({
              aartiContentId: Number(editingSubCategory.id),
              aartiId: Number(editingSubCategory.aartiCategoryId),
              aartiName: editingSubCategory.name,
              aartiContent: editingSubCategory.description,
              aartiImageUrl: editingSubCategory.imageUrl,
          })
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || 'Could not update sub-category.');
      
      toast.success('Sub-category updated successfully!');
      setOpenEditSubCategoryDialog(false);
      triggerRefetch();
    } catch (error) {
        console.error('API error:', error);
        toast.error(`Error: ${error.message}`);
    }
  };

  const handleDeleteSubCategory = async () => {
    if (!deletingSubCategory) return;
    try {
      const res = await fetch(`/api/aartiDetails/aarti/deleteAartiContentByAdmin/${deletingSubCategory.id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${getAuthToken()}` },
      });

      if (!res.ok) {
        const result = await res.json();
        throw new Error(result.message || 'Could not delete sub-category.');
      }

      toast.success('Sub-category deleted successfully!');
      setOpenDeleteSubCategoryDialog(false);
      triggerRefetch();
    } catch (error) {
        console.error('API error:', error);
        toast.error(`Error: ${error.message}`);
    }
  };

  // --- Dialog Open/Close Handlers ---
  const openEditCategory = (cat) => {
    setEditingCategory(cat);
    setOpenEditCategoryDialog(true);
  };
  
  const openDeleteCategory = (cat) => {
      setDeletingCategory(cat);
      setOpenDeleteCategoryDialog(true);
  };

  const openEditSubCategory = (sub) => {
    setEditingSubCategory(sub);
    setOpenEditSubCategoryDialog(true);
  };
  
  const openDeleteSubCategory = (sub) => {
      setDeletingSubCategory(sub);
      setOpenDeleteSubCategoryDialog(true);
  };

  const filteredSubCategories = selectedCategoryId
    ? subCategories.filter(sub => String(sub.aartiCategoryId) === String(selectedCategoryId))
    : [];

  const getSubCategoryCount = (categoryId) => {
      return subCategories.filter(sub => String(sub.aartiCategoryId) === String(categoryId)).length;
  }
  
  return (
    <Box sx={{ height: '100vh', overflow: 'hidden', p: 2 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight="600">
          Youtube Management
        </Typography>
        <Box display="flex" gap={2}>
          <Button variant="contained" color="primary" onClick={() => setOpenCategoryDialog(true)}
            sx={{ 
              minWidth: '180px',
              fontWeight: 'bold',
              textTransform: 'none'
            }}
            >
             + Add Youtube Video
          </Button>
        </Box>
      </Box>

      {/* Main Content Grid */}
      <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 100px)' }}>
        
        {/* Left Column - Categories */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" gutterBottom fontWeight="bold">Youtube Categories</Typography>
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {categories.length > 0 ? (
                            categories.map(cat => (
                                <Box
                                    key={cat.id}
                                    onClick={() => setSelectedCategoryId(cat.id)}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 2, p: 2, mb: 1,
                                        border: 1, borderRadius: 2,
                                        borderColor: selectedCategoryId === cat.id ? 'primary.main' : 'grey.300',
                                        backgroundColor: selectedCategoryId === cat.id ? 'primary.lightest' : 'white',
                                        cursor: 'pointer', '&:hover': { borderColor: 'primary.light', backgroundColor: 'grey.50' }
                                    }}
                                >
                                    <img src={cat.imageUrl || 'https://via.placeholder.com/60'} alt={cat.name} style={{ width: 60, height: 60, borderRadius: '8px', objectFit: 'cover' }}/>
                                    <Box flexGrow={1}>
                                        <Typography variant="subtitle1" fontWeight="bold">{cat.name}</Typography>
                                        <Typography variant="body2" color="text.secondary">ID: {cat.id}</Typography>
                                        <Typography variant="body2" color="text.secondary">Title Name: {cat.titleName}</Typography>
                                        <Typography variant="caption" color="primary">{getSubCategoryCount(cat.id)} Aarti(s)</Typography>
                                    </Box>
                                    <Box display="flex" flexDirection="column" gap={1}>
                                        <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openEditCategory(cat); }}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); openDeleteCategory(cat); }}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                            ))
                        ) : (
                            <Typography sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>No Aarti categories found.</Typography>
                        )}
                    </Box>
                </CardContent>
            </Card>
        </Box>

        {/* Right Column - Subcategories */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" gutterBottom fontWeight="bold">Youtube Content</Typography>
                     <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {filteredSubCategories.length > 0 ? (
                            filteredSubCategories.map(sub => (
                                <Box key={sub.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, mb: 1, border: 1, borderRadius: 2, borderColor: 'grey.200' }}>
                                    <img
                                        src={`https://img.youtube.com/vi/${extractYouTubeId(sub.videoUrl)}/0.jpg`}
                                        alt={sub.name}
                                        style={{ width: 80, height: 60, borderRadius: '6px', objectFit: 'cover' }}
                                    />
                                    <Box flexGrow={1}>
                                        <Typography variant="subtitle1" fontWeight="bold" color="secondary.dark">{sub.name}</Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">ID: {sub.id}</Typography>
                                        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                            {sub.description.split(' ').slice(0, 14).join(' ')}{sub.description.split(' ').length > 14 ? '...' : ''}
                                        </Typography>

                                    </Box>
                                     <Box display="flex" flexDirection="column" gap={1}>
                                        <IconButton size="small" color="primary" onClick={() => openEditSubCategory(sub)}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => openDeleteSubCategory(sub)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                            ))
                        ) : (
                             <Typography sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
                                {selectedCategoryId ? 'No Aarti content for this category.' : 'Please select a category.'}
                             </Typography>
                        )}
                    </Box>
                </CardContent>
            </Card>
        </Box>
      </Box>
      
      {/* --- Dialogs --- */}
      {/* (All dialogs remain the same) */}
      <Dialog open={openCategoryDialog} onClose={() => setOpenCategoryDialog(false)} fullWidth maxWidth="sm">
  <DialogTitle>Add Youtube Video</DialogTitle>
  <DialogContent>
    <TextField
      fullWidth
      label="Title"
      margin="normal"
      value={newCategory.titleName}
      onChange={(e) => setNewCategory({ ...newCategory, titleName: e.target.value })}
    />

    <TextField
      fullWidth
      label="Description"
      multiline
      rows={4}
      margin="normal"
      value={newSubCategory.description}
      onChange={(e) => setNewSubCategory({ ...newSubCategory, description: e.target.value })}
    />

    <TextField
      fullWidth
      label="YouTube Video URL"
      margin="normal"
      value={newSubCategory.videoUrl}
      onChange={(e) => setNewSubCategory({ ...newSubCategory, videoUrl: e.target.value })}
    />

    <Box mt={2}>
      <Typography variant="body2" gutterBottom>Upload Thumbnail Image</Typography>
      <input
        type="file"
        accept="image/*"
        onChange={(e) => setNewCategory({ ...newCategory, imageFile: e.target.files[0] })}
      />
    </Box>
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setOpenCategoryDialog(false)}>Cancel</Button>
    <Button onClick={handleCreateCategory} variant="contained">Create</Button>
  </DialogActions>
</Dialog>


      <Dialog open={openEditCategoryDialog} onClose={() => setOpenEditCategoryDialog(false)} fullWidth maxWidth="sm">
          <DialogTitle>Edit Aarti Category</DialogTitle>
          {editingCategory && (
              <>
                  <DialogContent>
                      <TextField fullWidth label="Category Name" margin="normal" value={editingCategory.name} onChange={e => setEditingCategory({ ...editingCategory, name: e.target.value })} />
                      <TextField fullWidth label="Image URL" margin="normal" value={editingCategory.imageUrl} onChange={e => setEditingCategory({ ...editingCategory, imageUrl: e.target.value })} />
                  </DialogContent>
                  <DialogActions>
                      <Button onClick={() => setOpenEditCategoryDialog(false)}>Cancel</Button>
                      <Button onClick={handleUpdateCategory} variant="contained">Save Changes</Button>
                  </DialogActions>
              </>
          )}
      </Dialog>
      
      <Dialog open={openDeleteCategoryDialog} onClose={() => setOpenDeleteCategoryDialog(false)} fullWidth maxWidth="xs">
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
              <Typography>Are you sure you want to delete the category: <strong>{deletingCategory?.name}</strong>?</Typography>
              <Typography color="error" variant="body2">This will also delete all associated Aarti content and cannot be undone.</Typography>
          </DialogContent>
          <DialogActions>
              <Button onClick={() => setOpenDeleteCategoryDialog(false)}>Cancel</Button>
              <Button onClick={handleDeleteCategory} variant="contained" color="error">Delete</Button>
          </DialogActions>
      </Dialog>
      
      <Dialog open={openEditSubCategoryDialog} onClose={() => setOpenEditSubCategoryDialog(false)} fullWidth maxWidth="sm">
          <DialogTitle>Edit Aarti Content</DialogTitle>
          {editingSubCategory && (
              <>
                  <DialogContent>
                      <FormControl fullWidth margin="normal">
                          <InputLabel id="edit-aarti-category-select-label">Category</InputLabel>
                          <Select
                              labelId="edit-aarti-category-select-label"
                              value={editingSubCategory.aartiCategoryId}
                              label="Category"
                              onChange={e => setEditingSubCategory({ ...editingSubCategory, aartiCategoryId: e.target.value })}
                          >
                              {categories.map(cat => (
                                  <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                              ))}
                          </Select>
                      </FormControl>
                      <TextField fullWidth label="Aarti Name" margin="normal" value={editingSubCategory.name} onChange={e => setEditingSubCategory({ ...editingSubCategory, name: e.target.value })} />
                      <TextField fullWidth label="Aarti Content / Description" multiline rows={4} margin="normal" value={editingSubCategory.description} onChange={e => setEditingSubCategory({ ...editingSubCategory, description: e.target.value })} />
                      <TextField fullWidth label="Image URL" margin="normal" value={editingSubCategory.imageUrl} onChange={e => setEditingSubCategory({ ...editingSubCategory, imageUrl: e.target.value })} />
                  </DialogContent>
                  <DialogActions>
                      <Button onClick={() => setOpenEditSubCategoryDialog(false)}>Cancel</Button>
                      <Button onClick={handleUpdateSubCategory} variant="contained">Save Changes</Button>
                  </DialogActions>
              </>
          )}
      </Dialog>
      
      <Dialog open={openDeleteSubCategoryDialog} onClose={() => setOpenDeleteSubCategoryDialog(false)} fullWidth maxWidth="xs">
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
              <Typography>Are you sure you want to delete the Aarti: <strong>{deletingSubCategory?.name}</strong>?</Typography>
               <Typography color="error" variant="body2">This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions>
              <Button onClick={() => setOpenDeleteSubCategoryDialog(false)}>Cancel</Button>
              <Button onClick={handleDeleteSubCategory} variant="contained" color="error">Delete</Button>
          </DialogActions>
      </Dialog>
          {/* Add ToastContainer at the end */}
            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />
    </Box>
  );
}